import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  RootState,
  useGetCurrentAgentQuery,
} from '../store';
import OrderSummary from '../components/OrderSummary';
import GotCoupon from '../components/GotCoupon';
import CouponDTO from '../types/Coupon';
import Button from '../components/atoms/Button';
import useQuery from '../hooks/useQuery';
import OpenPriceCard from '../components/OpenPriceCard';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetUserRole from '../hooks/useGetUserRole';
import TermsAndConditions from '../components/TermsAndConditions';
import TermsAndServicesType from '../models/terms';
import { PaymentType, PaymentProvider } from '../models/payment';
import {
  CheckoutContactForm,
  CheckoutPaymentOptions,
  checkoutValidationSchema,
  CheckoutFormData,
  shouldShowSubmitButton,
} from '../components/checkout';
import { useCheckoutPayment } from '../hooks/useCheckoutPayment';
import { trackInitiateCheckout, trackAddPaymentInfo } from '../utils/metaPixelTracking';

const CheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigateWithQuery();
  const query = useQuery();
  const isBackoffice = location.pathname.includes('/admin/');
  const role = isBackoffice ? useGetUserRole() : null;
  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const reservation = useSelector(
    (state: RootState) => state.orders.reservation,
  );
  const { orderItemData, pollData } = useSelector(
    (state: RootState) => state.orders,
  );

  // Query params
  const customized = query.get('customized') === 'true' || false;
  const skipPoll = query.get('skip-poll') === 'true' || false;
  const email = query.get('email') ?? null;
  const link = query.get('link') ?? null;
  const refCodeFromQuery = query.get('ref') ?? null;
  const refCodeFromStorage = event
    ? localStorage.getItem(`${event.id}_referral`)
    : null;
  const refCode = refCodeFromQuery || refCodeFromStorage;

  // Local state
  const [appliedCoupon, setAppliedCoupon] = useState<CouponDTO | null>(null);
  const [openPriceAmount, setOpenPriceAmount] = useState<number | undefined>(undefined);
  const [totalToPay, setTotalToPay] = useState<number | undefined>();
  const [totalsLoading, setTotalsLoading] = useState(true);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  // Get agent for manual payments
  const { data: agent } = useGetCurrentAgentQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '' },
    { skip: !event || !event.allow_manual_payments || isBackoffice },
  );

  // Redirect if no event or order data
  useEffect(() => {
    if (!event || !orderItemData) {
      navigate('../../', { replace: true });
    }
  }, [event, orderItemData, navigate]);

  if (!event || !orderItemData) {
    return null;
  }

  // Helper to get coupon code
  const getCouponCode = () => {
    try {
      const storedValue = JSON.parse(
        localStorage.getItem(`${event.id}_coupon`) || '{}',
      );
      if (!storedValue) {
        return query.get('coupon') ?? null;
      }
      return storedValue?.coupon?.code ?? query.get('coupon') ?? null;
    } catch (error) {
      console.warn('Failed to parse coupon from localStorage:', error);
      localStorage.removeItem(`${event.id}_coupon`);
      return query.get('coupon') ?? null;
    }
  };

  const couponCode = getCouponCode();
  const savedEmail = localStorage.getItem(`${event.id}_email`);

  // Form setup
  const form = useForm<CheckoutFormData>({
    defaultValues: {
      contactMethod: 'email',
      email: email ?? savedEmail ?? '',
      emailConfirm: email ?? savedEmail ?? '',
    },
    resolver: yupResolver<CheckoutFormData>(checkoutValidationSchema),
    mode: 'onChange',
  });

  const { handleSubmit } = form;

  // Derived values
  const selectedTickets = orderItemData.map((item) => ({
    ...item.ticket,
    audience_discount_id: item.audience_discount_id,
  }));
  const hasOpenPriceTicket = selectedTickets.find((t) => t.has_open_price);

  // Payment hook
  const {
    paymentMethod,
    setPaymentMethod,
    paymentProvider,
    setPaymentProvider,
    showPaymentOptions,
    clickedPay,
    isPayWayProcessing,
    setIsPayWayProcessing,
    isNaveProcessing,
    setIsNaveProcessing,
    navePaymentRequestId,
    orderData,
    isLoading,
    handlePay,
    handlePayWayTokenCreated,
    handlePayWayError,
    handleNavePaymentComplete,
    handleNaveError,
    getPayButtonLabel,
  } = useCheckoutPayment({
    event,
    orderItemData,
    pollData,
    reservation,
    isBackoffice,
    role,
    refCode,
    skipPoll,
    link,
    appliedCoupon,
    openPriceAmount,
    totalToPay,
    form,
    agent,
    navigate,
  });

  // Meta Pixel: InitiateCheckout when reaching the checkout page
  useEffect(() => {
    if (event && orderItemData && !isBackoffice) {
      trackInitiateCheckout({
        content_name: event.name,
        content_ids: Array.from(new Set(orderItemData.map((item) => item.ticket?.id).filter(Boolean))),
        value: orderItemData.reduce((sum, item) => sum + (item.ticket?.price || 0), 0),
        num_items: orderItemData.length,
      });
    }
  }, []);

  // Meta Pixel: AddPaymentInfo when payment provider is selected
  useEffect(() => {
    if (paymentProvider && !isBackoffice && event) {
      trackAddPaymentInfo({
        content_name: event.name,
        content_ids: Array.from(new Set(orderItemData.map((item) => item.ticket?.id).filter(Boolean))),
        value: totalToPay,
      });
    }
  }, [paymentProvider]);

  // Event handlers
  const handleCouponChange = (coupon: CouponDTO | null) => {
    setAppliedCoupon(coupon);
    localStorage.setItem(`${event.id}_coupon`, JSON.stringify({ coupon }));
  };

  const handleAmountChange = (amount: number | undefined) => {
    setOpenPriceAmount(amount);
  };

  const handleCalculatedTotalToPay = (total: number) => {
    setTotalToPay(total);
  };

  const handleBack = () => {
    navigate('../entry-data');
  };

  const handleProviderSelect = (
    provider: PaymentProvider | null,
    method: PaymentType | null,
  ) => {
    if (method === PaymentType.MANUAL) {
      setPaymentMethod(PaymentType.MANUAL);
      setPaymentProvider(null);
    } else {
      setPaymentProvider(provider);
      if (paymentMethod !== PaymentType.ONLINE) {
        setPaymentMethod(PaymentType.ONLINE);
      }
    }
  };

  const showCouponSection = !!((totalToPay && totalToPay > 0 && !hasOpenPriceTicket)
    || appliedCoupon);

  const showOnlinePaymentOptions = !!(!showPaymentOptions
    && !isBackoffice
    && totalToPay
    && totalToPay > 0);

  return (
    <form onSubmit={handleSubmit(handlePay)}>
      <div className="flex flex-col gap-[24px] items-center self-stretch">
        {/* Contact Form Section */}
        <CheckoutContactForm
          form={form}
          showPhoneOption={event.order_phone_number}
          isBackoffice={isBackoffice}
          disabled={clickedPay}
        />

        {/* Order Summary Section */}
        {orderData && (
          <OrderSummary
            order={orderData}
            event={event}
            onCalculatedTotalToPay={handleCalculatedTotalToPay}
            onLoadingTotals={setTotalsLoading}
            hasOpenPrice={!!hasOpenPriceTicket}
            items={orderItemData}
            customized={customized}
          />
        )}

        <div
          className="flex flex-col gap-[20px] items-start self-stretch"
          hidden={totalsLoading}
        >
          {/* Coupon Section */}
          {showCouponSection && (
            <div className="w-full rounded-3xl">
              <GotCoupon
                loadedCode={couponCode}
                onCouponChange={handleCouponChange}
              />
            </div>
          )}

          {/* Open Price Section */}
          {hasOpenPriceTicket && (
            <OpenPriceCard
              ticket={selectedTickets[0]}
              onAmountChange={handleAmountChange}
            />
          )}

          {/* Payment Options - With Manual Option */}
          {showPaymentOptions && (
            <CheckoutPaymentOptions
              paymentProvider={paymentProvider}
              paymentMethod={paymentMethod}
              showManualOption
              allowMercadoPago={event.allow_mercadopago_payments}
              allowUala={event.allow_uala_payments}
              allowPayWay={event.allow_payway_payments}
              allowNave={false} // nave embedded checkout disabled
              allowNaveLink
              allowZilo={event.allow_zilo_payments}
              disabled={clickedPay}
              onProviderSelect={handleProviderSelect}
              totalToPay={totalToPay}
              isBackoffice={isBackoffice}
              clickedPay={clickedPay}
              isPayWayProcessing={isPayWayProcessing}
              setIsPayWayProcessing={setIsPayWayProcessing}
              handlePayWayTokenCreated={handlePayWayTokenCreated}
              handlePayWayError={handlePayWayError}
              orderItemData={orderItemData.map((item) => ({
                name: item.name,
                last_name: item.last_name,
                person_id: item.person_id,
              }))}
              navePaymentRequestId={navePaymentRequestId}
              isNaveProcessing={isNaveProcessing}
              setIsNaveProcessing={setIsNaveProcessing}
              handleNavePaymentComplete={handleNavePaymentComplete}
              handleNaveError={handleNaveError}
            />
          )}

          {/* Payment Options - Without Manual Option */}
          {showOnlinePaymentOptions && (
            <CheckoutPaymentOptions
              paymentProvider={paymentProvider}
              paymentMethod={paymentMethod}
              showManualOption={false}
              allowMercadoPago={event.allow_mercadopago_payments}
              allowUala={event.allow_uala_payments}
              allowPayWay={event.allow_payway_payments}
              allowNave={event.allow_nave_payments}
              allowNaveLink
              allowZilo={event.allow_zilo_payments}
              disabled={clickedPay}
              onProviderSelect={handleProviderSelect}
              totalToPay={totalToPay}
              isBackoffice={isBackoffice}
              clickedPay={clickedPay}
              isPayWayProcessing={isPayWayProcessing}
              setIsPayWayProcessing={setIsPayWayProcessing}
              handlePayWayTokenCreated={handlePayWayTokenCreated}
              handlePayWayError={handlePayWayError}
              orderItemData={orderItemData.map((item) => ({
                name: item.name,
                last_name: item.last_name,
                person_id: item.person_id,
              }))}
              navePaymentRequestId={navePaymentRequestId}
              isNaveProcessing={isNaveProcessing}
              setIsNaveProcessing={setIsNaveProcessing}
              handleNavePaymentComplete={handleNavePaymentComplete}
              handleNaveError={handleNaveError}
            />
          )}

          {isLoading && (
            <div className="w-full rounded-3xl bg-surface-secondary border border-border-primary p-5 flex flex-col items-center gap-3 animate-pulse">
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-medium text-text-primary">
                  Preparando tu pago...
                </span>
              </div>
              <p className="text-xs text-text-tertiary text-center">
                Estamos creando todos los datos necesarios. Este proceso puede tardar unos segundos.
              </p>
              <div className="w-full bg-border-secondary rounded-full h-1 overflow-hidden">
                <div className="bg-primary-600 h-1 rounded-full animate-indeterminate" />
              </div>
            </div>
          )}

          {/* Terms and Conditions Link */}
          {!isBackoffice && (
            <div className="w-full text-center text-sm text-text-secondary">
              Al continuar, aceptas nuestros{' '}
              <button
                type="button"
                className="text-primary underline"
                onClick={() => setTermsModalOpen(true)}
              >
                términos y condiciones
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse md:flex-row gap-4 justify-center w-full">
          <Button variant="secondary" onClick={handleBack} type="button">
            Volver
          </Button>
          {shouldShowSubmitButton(paymentProvider, paymentMethod, totalToPay, isBackoffice, navePaymentRequestId) && (
            <Button
              variant="primary"
              loading={isLoading || totalsLoading}
              disabled={hasOpenPriceTicket && !openPriceAmount}
              type="submit"
            >
              {getPayButtonLabel()}
            </Button>
          )}
        </div>
      </div>

      <TermsAndConditions
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        type={TermsAndServicesType.BUYER}
      />
    </form>
  );
};

export default CheckoutPage;
